import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { ZoomRound, ZoomQuestState } from '../../../interfaces/zoom-quest.interface';
import { ZoomQuestStageComponent } from './zoom-quest-stage.component';

interface DigitCell { char: string; comma: boolean; place: number; }

const PLACE_NAMES: { en: string; ar: string }[] = [
  { en: 'ones', ar: 'الآحاد' },
  { en: 'tens', ar: 'العشرات' },
  { en: 'hundreds', ar: 'المئات' },
  { en: 'thousands', ar: 'الآلاف' },
  { en: 'ten-thousands', ar: 'عشرات الآلاف' }
];

/**
 * Host shell for Zoom Quest. Reads `ZoomQuestState`, shows the target with a
 * place-value banner that lights up the digit currently being resolved (it
 * tracks the stage's zoom depth), and drives ZOOM IN / OUT / PLANT on the
 * stage via @ViewChild. Outcomes are written with `scoreZoomQuestRound` +
 * `advanceZoomQuestRound`.
 */
@Component({
  selector: 'app-zoom-quest-game',
  templateUrl: './zoom-quest-game.component.html',
  styleUrls: ['./zoom-quest-game.component.scss']
})
export class ZoomQuestGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(ZoomQuestStageComponent) stage?: ZoomQuestStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): ZoomQuestState | null {
    return (this.session?.gameState as ZoomQuestState) ?? null;
  }
  get round(): ZoomRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Place-value banner ─────────────────────────────────────────────

  /** How many place values are already resolved (= current zoom depth). */
  get activePlaceFromLeft(): number { return this.stage?.levelIndex ?? 0; }

  /** Target digits (with thousands commas) tagged with their place value. */
  get digitCells(): DigitCell[] {
    const r = this.round;
    if (!r) return [];
    const digits = String(r.target).split('');
    const n = digits.length;
    const cells: DigitCell[] = [];
    digits.forEach((char, i) => {
      const place = n - 1 - i; // 0 = ones
      cells.push({ char, comma: false, place });
      const remaining = n - 1 - i;
      if (remaining > 0 && remaining % 3 === 0) {
        cells.push({ char: ',', comma: true, place: -1 });
      }
    });
    return cells;
  }
  /** True when this digit's place is the one currently being zoomed into. */
  isActiveCell(cell: DigitCell): boolean {
    if (cell.comma || !this.round) return false;
    const active = this.round.digits - 1 - this.activePlaceFromLeft;
    return cell.place === active;
  }
  /** True once we've zoomed past this digit's place value. */
  isLockedCell(cell: DigitCell): boolean {
    if (cell.comma || !this.round) return false;
    const active = this.round.digits - 1 - this.activePlaceFromLeft;
    return cell.place > active;
  }

  get activePlaceName(): string {
    const r = this.round;
    if (!r) return '';
    const place = r.digits - 1 - this.activePlaceFromLeft;
    const name = PLACE_NAMES[place] ?? PLACE_NAMES[0];
    return this.currLang === 'ar' ? name.ar : name.en;
  }

  // ── Controls ───────────────────────────────────────────────────────

  onZoomIn(): void {
    if (this.resolving || !this.stage?.canZoomIn) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.zoomIn();
  }

  onZoomOut(): void {
    if (this.resolving || !this.stage?.canZoomOut) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.zoomOut();
  }

  onPlant(): void {
    if (this.resolving || !this.stage?.canPlant) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.plant();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreZoomQuestRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceZoomQuestRound(), 3000);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong dig — buzz and let them zoom out / re-place. Nothing revealed.
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreZoomQuestRound(false);
    this.later(() => this.miniGameService.advanceZoomQuestRound(), 250);
  }
}
